// TODO: Unify with design-system Button

import _ from 'lodash';
import React from 'react';
import Link from '@scale/frontend-lib/link';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface IButtonProps {
  type?: string;
  size?: string;
  arrows?: boolean;
  block?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  tooltip?: string;
  value?: string;
  requireConfirmation?: boolean;
  confirmationMessage?: string;
  primary?: boolean;
  success?: boolean;
  danger?: boolean;
  green?: boolean;
  yellow?: boolean;
  red?: boolean;
  short?: boolean;
  square?: boolean;
  extraShadow?: boolean;
  noBorders?: boolean;
  useRegularLink?: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLElement>;
  textTransform?: string;
  icon?: React.ReactNode;
  buttonType?: 'button' | 'submit' | 'reset';
  form?: string;
  className?: string;
  href?: string;
  title?: string;
  prefetch?: boolean;
  target?: string;
  dropdownContent?: React.ReactNode;
  rel?: string;
  children?: React.ReactNode;
}

interface IButtonState {
  dropdownOpen: boolean;
}

/** @deprecated - prefer `design-system/Button` */
export default class Button extends React.Component<IButtonProps, IButtonState> {
  static defaultProps = {
    type: 'default',
    size: 'md',
    arrows: false,
    block: false,
    green: false,
    disabled: false,
    tooltip: null,
    textTransform: 'uppercase',
    useRegularLink: false,
    onClick: _.noop,
    onMouseEnter() {},
    onMouseLeave() {},
  };

  constructor(props: IButtonProps) {
    super(props);

    this.state = {
      dropdownOpen: false,
    };
  }

  toggleDropdown = () => {
    this.setState(prevState => ({
      dropdownOpen: !prevState.dropdownOpen,
    }));
  };

  handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!this.props.onClick) {
      return;
    }

    if (this.props.requireConfirmation) {
      const confirmationMessage = this.props.confirmationMessage || 'Are you sure?';
      if (!window.confirm(confirmationMessage)) {
        return;
      }
    }

    this.props.onClick(event);
  };

  render() {
    const { textTransform, buttonType, form } = this.props;

    const buttonClass = classNames(this.props.type, this.props.size, {
      btn: true,
      primary: this.props.primary,
      success: this.props.success,
      danger: this.props.danger,

      green: this.props.green,
      yellow: this.props.yellow,
      red: this.props.red,

      disabled: this.props.disabled,
      short: this.props.short,
      square: this.props.square,
      extraShadow: this.props.extraShadow,
      'no-borders': this.props.noBorders,
    });

    let children = this.props.children;

    if (this.props.type === 'dropdown') {
      children = (
        <div>
          {this.props.children}
          <svg viewBox="0 0 100 100">
            <path d="m89.324 24.074h-78.598c-5.3711 0-7.2148 2.6133-4.4492 5.9883l38.531 43.445c2.7656 3.2227 7.5195 3.2227 10.438 0l38.379-43.445c2.9141-3.2188 1.0703-5.9883-4.3008-5.9883z" />
          </svg>
          <style jsx>{`
            svg {
              width: 10px;
              margin-left: 10px;
            }
          `}</style>
        </div>
      );
    }

    if (this.props.icon) {
      children = (
        <div>
          <span>{this.props.icon}</span> {this.props.children}
          <style jsx>{`
            div {
              display: flex;
              align-items: center;
            }
            span {
              margin-right: 6px;
            }
          `}</style>
        </div>
      );
    }

    if (this.props.arrows) {
      children = (
        <div>
          {this.props.children}
          <span style={{ marginLeft: '8px', color: this.props.success ? '#00B8F4' : 'inherit' }}>
            <span style={{ marginLeft: '4px', opacity: 0.5, display: 'inline-block' }}>
              <FontAwesomeIcon icon="caret-right" />
            </span>
            <span style={{ marginLeft: '4px', opacity: 0.7, display: 'inline-block' }}>
              <FontAwesomeIcon icon="caret-right" />
            </span>
            <span style={{ marginLeft: '4px', opacity: 0.9, display: 'inline-block' }}>
              <FontAwesomeIcon icon="caret-right" />
            </span>
          </span>
        </div>
      );
    }

    let buttonToRender;
    if (this.props.type === 'button') {
      buttonToRender = 'button';
    } else if (this.props.type === 'submit') {
      buttonToRender = 'submit';
    } else if (this.props.type === 'dropdown') {
      buttonToRender = 'dropdown';
    } else if (this.props.href && !this.props.useRegularLink) {
      buttonToRender = 'prefetch-link';
    } else {
      buttonToRender = 'link';
    }

    return (
      <span data-tip={this.props.tooltip} className={this.props.className}>
        {buttonToRender === 'button' && (
          <button
            style={this.props.style}
            disabled={this.props.disabled}
            className={buttonClass}
            onClick={this.handleClick}
            onMouseEnter={this.props.onMouseEnter}
            onMouseLeave={this.props.onMouseLeave}
            type={buttonType}
            form={form}
            title={this.props.title}
          >
            {children}
          </button>
        )}

        {buttonToRender === 'submit' && (
          <input
            style={this.props.style}
            className={buttonClass}
            type="submit"
            value={this.props.value}
          />
        )}

        {buttonToRender === 'dropdown' && (
          <div className="dropdown-container">
            <button
              style={this.props.style}
              disabled={this.props.disabled}
              className={buttonClass}
              onClick={this.toggleDropdown}
            >
              {children}
            </button>
            <div
              style={{ display: this.state.dropdownOpen === true ? 'block' : 'none' }}
              className={'dropdown-content'}
            >
              {this.props.dropdownContent}
            </div>
          </div>
        )}

        {buttonToRender === 'prefetch-link' && (
          <Link href={this.props.href!}>
            <a
              style={this.props.style}
              className={buttonClass}
              onClick={this.handleClick}
              onMouseEnter={this.props.onMouseEnter}
              onMouseLeave={this.props.onMouseLeave}
              rel={this.props.rel}
            >
              {children}
            </a>
          </Link>
        )}

        {buttonToRender === 'link' && (
          <a
            href={this.props.href}
            target={this.props.target}
            style={this.props.style}
            className={buttonClass}
            onClick={this.handleClick}
            onMouseEnter={this.props.onMouseEnter}
            onMouseLeave={this.props.onMouseLeave}
            rel={this.props.rel}
          >
            {children}
          </a>
        )}

        <style jsx>{`
          span.full {
            display: flex;
            flex-direction: column;
            align-items: stretch;
          }

          span.full .btn {
            margin: 0;
          }

          button,
          input {
            font-family: var(--font-family);
          }

          button,
          input {
            border: 0;
            outline: 0;
          }

          .btn {
            border: 0;
            outline: 0;
            text-transform: ${textTransform};
            letter-spacing: ${textTransform === 'uppercase' ? '0.5px' : 'auto'};
            border-radius: var(--border-radius, 8px);
            background: white;
            color: #000;
            fill: black;
            font-size: 14px;
            font-weight: 500;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            white-space: nowrap;

            cursor: pointer;
            border: none;
            transition: 0.2s ease-out all;
            box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.03), 0 1px 0 rgba(0, 0, 0, 0.05),
              0 1px 3px rgba(0, 0, 0, 0.1);
          }

          @media only screen and (min-width: 768px) {
            .btn {
              font-family: var(--font-family);
            }
          }

          .btn:hover,
          .btn:focus {
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }

          /* Sizes */
          .sm {
            font-size: 13px;
            padding: 5px 10px;
          }

          .md {
            font-size: 14px;
            padding: 7px 12px;
          }

          .lg {
            font-size: 14px;
            padding: 14px 20px;
          }

          .full-width {
            width: 100%;
          }

          .no-borders {
            box-shadow: none;
          }

          .green {
            background: #00ff07;
            color: #333;
            fill: #333;
          }

          .green:hover {
            background: rgb(0, 230, 6);
          }

          .yellow {
            background: #ffca28;
            color: #333;
            fill: #333;
          }

          .red {
            background: var(--color-reject);
            color: white;
            fill: white;
          }

          .purple {
            background: #8215fe;
            color: white;
            fill: white;
          }

          .gradient {
            background-image: linear-gradient(310deg, #8e0cff 0%, #ff4de9 40%, #f89558 100%);
            color: white;
            fill: white;
          }

          .remoBlue {
            background: var(--color-remo-blue);
            color: white;
            fill: white;
          }

          /* Button Types */
          .primary {
            background: #2773ff;
            color: white;
            fill: white;
            box-shadow: 0 3px 4px rgba(0, 0, 0, 0.1);
          }
          .primary:hover {
            background: rgb(35, 104, 230);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
          }

          .secondary {
            background: rgba(43, 153, 216, 0.2);
            color: #2b99d8;
            letter-spacing: normal;
            fill: white;
            box-shadow: 0 3px 4px rgba(0, 0, 0, 0.1);
          }

          .secondary:hover {
            background: rgba(43, 153, 216, 0.2);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
          }

          .modal {
            background: var(--color-pink);
            color: white;
          }

          .modal:hover {
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
          }

          .success {
            background: black;
            color: white;
            fill: white;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          }

          .success:hover {
            box-shadow: 0 3px 10px rgba(0, 0, 0, 0.4);
          }

          .successAlt {
            background: rgb(23, 182, 41);
            color: white;
            fill: white;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          }

          .successAlt:hover {
            background: rgb(20, 159, 36);
            box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
          }

          .danger {
            background: rgb(236, 50, 47);
            color: white;
            fill: white;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          }

          .danger:hover {
            background: rgb(189, 40, 38);
            box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
          }

          .hover-danger:hover {
            color: white;
            fill: white;
            background: rgb(188, 67, 29);
            box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
          }

          .dropdown,
          .dropdown-content {
            border: 1px solid rgba(0, 0, 0, 0.05);
          }

          .link {
            background: none;
            box-shadow: none;
            text-transform: none;
            letter-spacing: 0;
            padding: 0;
            color: var(--link-color);
          }

          .link:hover {
            box-shadow: none;
          }

          .dropdown-container .dropdown-content {
            margin-top: 10px;
            border-radius: 8px;
            box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
          }

          /* Deprecated */
          .info {
            background: green;
            color: white;
            box-shadow: 0 5px 10px rgba(0, 0, 0, 0.2);
          }

          .short {
            height: 35px;
            padding: 10px 22px;
          }

          .square {
            padding: 0;
            height: 50px;
            min-width: 50px;
          }

          .disabled {
            background: rgba(0, 0, 0, 0);
            pointer-events: none;
            color: rgba(0, 0, 0, 0.2);
          }

          .extraShadow {
            box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.4);
          }

          .extraShadow:hover {
            box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
          }
        `}</style>
      </span>
    );
  }
}
